'use client';

import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to calculate ETA using Google Maps Distance Matrix API
 * @param {Object} origin - { lat, lng } of origin
 * @param {Object} destination - { lat, lng } of destination
 * @param {boolean} enabled - Whether to fetch ETA
 * @returns {Object} - { eta, distance, duration, loading, error }
 */
export function useETACalculation(origin, destination, enabled = true) {
  const [data, setData] = useState({
    eta: null,
    distance: null,
    duration: null,
    durationText: null,
    distanceText: null,
    loading: false,
    error: null,
  });

  const calculateETA = useCallback(async () => {
    if (!enabled || !origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) {
      return;
    }

    // Check if Google Maps is loaded
    if (typeof window === 'undefined' || !window.google?.maps) {
      setData((prev) => ({ ...prev, error: 'Google Maps not loaded' }));
      return;
    }

    setData((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const service = new window.google.maps.DistanceMatrixService();

      const response = await new Promise((resolve, reject) => {
        service.getDistanceMatrix(
          {
            origins: [{ lat: origin.lat, lng: origin.lng }],
            destinations: [{ lat: destination.lat, lng: destination.lng }],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.METRIC,
          },
          (response, status) => {
            if (status === 'OK') {
              resolve(response);
            } else {
              reject(new Error(`Distance Matrix API error: ${status}`));
            }
          }
        );
      });

      const result = response.rows[0]?.elements[0];

      if (result?.status === 'OK') {
        // Calculate ETA based on duration
        const durationSeconds = result.duration.value;
        const now = new Date();
        const eta = new Date(now.getTime() + durationSeconds * 1000);

        setData({
          eta: eta,
          distance: result.distance.value, // in meters
          duration: result.duration.value, // in seconds
          durationText: result.duration.text,
          distanceText: result.distance.text,
          loading: false,
          error: null,
        });
      } else {
        setData((prev) => ({
          ...prev,
          loading: false,
          error: 'Unable to calculate route',
        }));
      }
    } catch (error) {
      console.error('[useETACalculation] Error:', error);
      setData((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to calculate ETA',
      }));
    }
  }, [origin, destination, enabled]);

  useEffect(() => {
    calculateETA();
  }, [calculateETA]);

  const refresh = useCallback(() => {
    calculateETA();
  }, [calculateETA]);

  return { ...data, refresh };
}

/**
 * Format ETA for display
 * @param {Date} eta - ETA date object
 * @returns {string} - Formatted ETA string
 */
export function formatETA(eta) {
  if (!eta) return 'N/A';

  const now = new Date();
  const diffMs = eta.getTime() - now.getTime();

  if (diffMs < 0) {
    return 'Arriving now';
  }

  const diffMins = Math.round(diffMs / (1000 * 60));

  if (diffMins < 60) {
    return `${diffMins} min${diffMins !== 1 ? 's' : ''}`;
  }

  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;

  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  // Format as time for longer durations
  return eta.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export default useETACalculation;
